import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from 'react-query';
import { DefaultGetResponse } from '~/App/Api/rest';
import { Subscription } from '~/types/models';
import axios, { AxiosError } from 'axios';
import { billingApi } from '../utils';
import { useContext } from 'react';
import FlashContext from '~/App/Components/Flash/Context';
import { confirmAsync } from '~/App/Components/Modals/Confirm';

export const queryKeys = {
  all: ['subscriptions'] as const,
  lists: () => [...queryKeys.all, 'lists'] as const,
  details: () => [...queryKeys.all, 'details'] as const,
  detail: (slug: string) => [...queryKeys.details(), slug] as const,
};

export async function getSubscription(planSlug: string) {
  const res = await axios.get<Subscription>(
    `${billingApi}/subscriptions/${planSlug}`
  );

  return res.data;
}

export interface UseSubscriptionParams {
  options?: UseQueryOptions<DefaultGetResponse<Subscription>, AxiosError>;
  slug: string;
}
export const useSubscription = ({ options, slug }: UseSubscriptionParams) =>
  useQuery<DefaultGetResponse<Subscription>, AxiosError>(
    queryKeys.detail(slug),
    () => getSubscription(slug),
    options
  );

export async function getSubscriptions() {
  const res = await axios.get<Subscription[]>(`${billingApi}/subscriptions`);

  return res.data;
}

export interface UseSubscriptionsParams {
  options?: UseQueryOptions<DefaultGetResponse<Subscription[]>, AxiosError>;
}

export const useSubscriptions = ({ options }: UseSubscriptionsParams) =>
  useQuery<DefaultGetResponse<Subscription[]>, AxiosError>(
    queryKeys.lists(),
    getSubscriptions,
    options
  );

export interface ResumeSubscriptionResponse {
  message: string;
}

export async function resumeSubscription(planSlug: string) {
  const res = await axios.post<ResumeSubscriptionResponse>(
    `${billingApi}/subscription/resume/${planSlug}`
  );
  return res.data;
}

export interface UseResumeSubscriptionParams {
  options?: UseMutationOptions<ResumeSubscriptionResponse, AxiosError, string>;
}

export const useResumeSubscription = ({
  options,
}: UseResumeSubscriptionParams) =>
  useMutation(resumeSubscription, { ...options });

export async function resumeAddOnSubscription(planUuid: string) {
  const res = await axios.post<ResumeSubscriptionResponse>(
    `${billingApi}/subscription/add-on/resume/${planUuid}`
  );
  return res.data;
}

export const useResumeAddOnSubscription = ({
  options,
}: UseResumeSubscriptionParams) =>
  useMutation(resumeAddOnSubscription, { ...options });

export const useResumeAddOn = (addOnUuid?: string) => {
  const { createFlash } = useContext(FlashContext);

  if (!addOnUuid) {
    return {
      onResumeClick: () => undefined,
    };
  }

  const { mutateAsync: resumeAddOnSubscription, isLoading: isResumingAddOn } =
    useResumeAddOnSubscription({
      options: {
        onSuccess: () => {
          createFlash({
            id: 'resume-subscription-success',
            title: 'Success',
            message: 'Add-on resumed successfully',
          });

          window.location.reload();
        },
        onError: ({ message }) => {
          createFlash({
            id: 'resume-subscription-error',
            title: 'Something went wrong',
            message: message ?? 'Error',
            options: { style: 'danger' },
          });
        },
      },
    });

  const onResumeClick = async () => {
    if (
      await confirmAsync(
        'Are you sure you want to resume this add-on?',
        'Yes, resume add-on',
        'No, cancel'
      )
    ) {
      await resumeAddOnSubscription(addOnUuid);
    }
  };

  return {
    onResumeClick,
    isResumingAddOn,
  };
};
